import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import moment, { Moment, MomentInput } from 'moment'
import React, { startTransition, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useSelector } from 'react-redux'
import SetupExpenseReportCycleModal from 'src/component-modal/SetupExpenseReportCycleModal/SetupExpenseReportCycleModal'
import { UtilityToolbarDropdownChildProps } from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarDropdown'
import DatesPickerDropdown from 'src/components/DatesPickerDropdown/DatesPickerDropdown'
import SvgArrowBack from 'src/components/Svgs/SvgArrowBack'
import { useTranslate } from 'src/localization/Trns'
import { OrgExpenseReport } from 'src/models/orgExpenseReports.models'
import { selectBrandColorCta } from 'src/redux/selectors/session.selectors'

import style from './DateFilterDropdown.style'

interface DateFilterDropdownProps extends UtilityToolbarDropdownChildProps {
  dateRangeType?: DateRangeType
  handleSetType: (type: DateRangeType | undefined) => void
  handleSetRange: (since: Moment, until: Moment) => void
  handleSetSince: (val: Moment | undefined) => void
  handleSetUntil: (val: Moment | undefined) => void
  customSince: Moment | undefined
  customUntil: Moment | undefined
  showStatementOptions: boolean | undefined
  cycleId?: string
  currentStatement?: OrgExpenseReport
  previousStatement?: OrgExpenseReport
}

export enum DateRangeType {
  Today = 'Today',
  ThisMonth = 'This Month',
  LastMonth = 'Last Month',
  CurrentStatement = 'Current Statement',
  PreviousStatement = 'Previous Statement',
  Custom = 'Custom Date Range',
}

const DateFilterDropdown: React.FC<DateFilterDropdownProps> = (
  props,
): React.ReactElement => {
  const brandColorCta = useSelector(selectBrandColorCta)

  const translate = useTranslate()

  const [showDatePicker, setShowDatePicker] = useState(false)
  const prevOpenRef = useRef(props.open)

  const [showSetupCyclePopup, setShowSetupCyclePopup] = useState(false)

  useEffect(() => {
    if (prevOpenRef.current && !props.open) {
      setTimeout(() => {
        setShowDatePicker(false)
      }, 300)
    }
    prevOpenRef.current = props.open
  }, [props.open])

  const needsCycleSetup =
    props.showStatementOptions && props.currentStatement === undefined

  const formatRange = (
    startInput?: MomentInput,
    endInput?: MomentInput,
    fallback = '',
  ): string => {
    if (!startInput || !endInput) return fallback
    const start = moment(startInput)
    const end = moment(endInput)
    if (!start.isValid() || !end.isValid()) return fallback

    const sameYear = start.year() === end.year()
    const startFmt = sameYear ? 'MMM D' : 'MMM D, YYYY'
    const endFmt = 'MMM D, YYYY'
    return `${start.format(startFmt)} – ${end.format(endFmt)}`
  }

  const monthRangeText = (m: moment.Moment) =>
    formatRange(m.clone().startOf('month'), m.clone().endOf('month'))

  useEffect(() => {
    if (!props.showStatementOptions && props.dateRangeType !== undefined) {
      // switch to custom if user was previously on statement option
      props.handleSetType(DateRangeType.Custom)
    }
  }, [props.showStatementOptions])

  const handleFixedRangeClick = (
    e: React.SyntheticEvent,
    type: DateRangeType,
  ): void => {
    e.stopPropagation()

    flushSync(() => {
      setShowDatePicker(false)
      props.close?.()
    })

    startTransition(() => {
      // setDateRangeType(type)
      props.handleSetType(type)

      const now = moment()

      switch (type) {
        case DateRangeType.Today:
          props.handleSetRange(
            now.clone().startOf('day'),
            now.clone().endOf('day'),
          )
          break
        case DateRangeType.ThisMonth:
          props.handleSetRange(
            now.clone().startOf('month'),
            now.clone().endOf('month'),
          )
          break
        case DateRangeType.LastMonth: {
          const lastMonth = now.clone().subtract(1, 'month')
          props.handleSetRange(
            lastMonth.clone().startOf('month'),
            lastMonth.clone().endOf('month'),
          )
          break
        }
        case DateRangeType.CurrentStatement: {
          if (needsCycleSetup) {
            setShowSetupCyclePopup(true)
          } else {
            props.handleSetType(DateRangeType.CurrentStatement)
            props.handleSetRange(
              moment(props.currentStatement?.since),
              moment(props.currentStatement?.until),
            )
          }
          break
        }
        case DateRangeType.PreviousStatement: {
          if (needsCycleSetup) {
            setShowSetupCyclePopup(true)
          } else {
            props.handleSetType(DateRangeType.PreviousStatement)
            props.handleSetRange(
              moment(props.previousStatement?.since),
              moment(props.previousStatement?.until),
            )
          }
          break
        }
        case DateRangeType.Custom:
          setShowDatePicker(false)
          break
      }
    })
  }

  const handleReset = (): void => {
    flushSync(() => {
      setShowDatePicker(false)
      props.close?.()
    })

    startTransition(() => {
      props.handleSetType(undefined)
    })
  }

  const handleSetSince = (val: Moment | undefined): void => {
    props.handleSetType(DateRangeType.Custom)
    props.handleSetSince(val)
  }

  const handleSetUntil = (val: Moment | undefined): void => {
    props.handleSetType(DateRangeType.Custom)
    props.handleSetUntil(val)
    props.close?.()
  }

  const now = moment()

  const today = now.format('MMM D, YYYY')

  const thisMonthText = monthRangeText(now)
  const lastMonthText = monthRangeText(now.clone().subtract(1, 'month'))

  const setupFallback = translate('Set up your card account statement cycle')

  const currentStatementText = props.currentStatement
    ? formatRange(
        props.currentStatement.since,
        props.currentStatement.until,
        setupFallback,
      )
    : setupFallback

  const previousStatementText = props.previousStatement
    ? formatRange(
        props.previousStatement.since,
        props.previousStatement.until,
        setupFallback,
      )
    : setupFallback

  const customRangeText = formatRange(props.customSince, props.customUntil)

  const options = [
    {
      type: DateRangeType.Today,
      value: today,
    },
    {
      type: DateRangeType.ThisMonth,
      value: thisMonthText,
    },
    {
      type: DateRangeType.LastMonth,
      value: lastMonthText,
    },
    ...(props.showStatementOptions
      ? [
          {
            type: DateRangeType.CurrentStatement,
            value: currentStatementText,
          },
        ]
      : []),
    // we want to hide this if there is no previous report but the cycle has been setup
    ...((props.showStatementOptions && props.previousStatement !== undefined) ||
    needsCycleSetup
      ? [
          {
            type: DateRangeType.PreviousStatement,
            value: previousStatementText,
          },
        ]
      : []),
  ]

  return (
    <div className="DateFilterDropdown" css={style}>
      <div
        className={cn('DateFilterDropdown', 'container', {
          large: showDatePicker,
          small: !showDatePicker,
        })}
      >
        {!showDatePicker && (
          <>
            {options.map((option, index) => (
              <div
                className={cn('option', {
                  active: props.dateRangeType === option.type,
                })}
                key={option.type}
                onClick={(e): void => handleFixedRangeClick(e, option.type)}
                onKeyDown={(e): void => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleFixedRangeClick(e, option.type)
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <span className="option-type">{option.type}</span>
                <span className="option-value">{option.value}</span>
              </div>
            ))}
            <div
              key={DateRangeType.Custom}
              className={cn('custom-option', {
                active: props.dateRangeType === DateRangeType.Custom,
              })}
              role="button"
              tabIndex={0}
              onClick={(e): void => setShowDatePicker(true)}
              onKeyDown={(e): void => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setShowDatePicker(true)
                }
              }}
            >
              {translate('Custom Date Range')}
              {props.dateRangeType === DateRangeType.Custom && (
                <span className={'option-value'}>{customRangeText}</span>
              )}
            </div>
          </>
        )}
        {showDatePicker && (
          <div>
            <button
              className="back-button"
              tabIndex={0}
              onClick={(): void => setShowDatePicker(false)}
            >
              <SvgArrowBack
                width={20}
                height={20}
                viewBox={'0 0 24 24'}
                fill={brandColorCta}
              />
              <span>{translate('Back')}</span>
            </button>
            <DatesPickerDropdown
              since={
                props.dateRangeType === DateRangeType.Custom
                  ? props.customSince
                  : undefined
              }
              until={
                props.dateRangeType === DateRangeType.Custom
                  ? props.customUntil
                  : undefined
              }
              setSince={handleSetSince}
              setUntil={handleSetUntil}
              disabled={false}
              startAndKeepOpen={true}
              hideLabel={true}
              hideShadow={true}
            />
          </div>
        )}
        <div className={'reset-container'}>
          <Button
            onClick={(): void => handleReset()}
            className={'reset-button'}
            variant={'no-border'}
          >
            {translate('Reset to Last 30 Days')}
          </Button>
        </div>
      </div>
      <SetupExpenseReportCycleModal
        isOpen={showSetupCyclePopup}
        onClose={() => setShowSetupCyclePopup(false)}
        cycleId={props.cycleId || ''}
      />
    </div>
  )
}

export default DateFilterDropdown
